import { Schema } from 'effect';
import { PlacementIdSchema } from './PlacementId';
export { decodePlacementId } from './PlacementId';

/**
 * The one shape every carrier's pickup point reduces to.
 *
 * Each carrier answers with its own wire shape, and each names the field the
 * backend wants differently: Packeta sends its point id, PPL sends a `code`,
 * Balíkovna and Slovak Post send the postcode. The gateway that talks to a
 * carrier is the only code that knows which — it maps its own payload into this
 * value object, and everything above it works with one type.
 *
 * `placementId` is the validated value sent to the backend shipment.
 */
export const PickupCarrier = {
  Packeta: 'packeta',
  Balikovna: 'balikovna',
  Ppl: 'ppl',
  SlovakPost: 'slovakPost',
} as const;

export type PickupCarrier = (typeof PickupCarrier)[keyof typeof PickupCarrier];

export const PickupCarrierSchema = Schema.Literals(Object.values(PickupCarrier));

/**
 * The codec for a pickup point.
 *
 * A point outlives the step that chose it. It is written into the order
 * shipment cookie, so the checkout can redraw the pickup box after the shopper
 * leaves the shipping step and comes back.
 */
export const PickupPointSchema = Schema.Struct({
  carrier: PickupCarrierSchema,
  placementId: PlacementIdSchema,
  name: Schema.String,
  /** Balíkovna and Slovak Post answer with a single address line; it lands here. */
  street: Schema.optionalKey(Schema.String),
  /** Balíkovna does not report a city. */
  city: Schema.optionalKey(Schema.String),
  /** Some carriers can omit the postcode in their selection payload. */
  zip: Schema.optionalKey(Schema.String),
});

export type PickupPoint = Schema.Schema.Type<typeof PickupPointSchema>;
